import { createTheme, ThemeProvider } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import {
    Route,
    Routes,
    useLocation,
    useNavigate,
    Navigate,
} from "react-router-dom";

//? COMPONENTS //
import Header from "components/header/header";
import Login from "pages/auth/login/login";
import Unauthorized from "pages/home/unauthorized";
import Content from "pages/content/content";
import Home from "pages/home/home";
import ZeroStep from "pages/requests/stepsIndividual/standalone-step0/step0";
import AanvraagVeiligheidsVerificatieIndienen from "pages/requests/formRequestIndividual";
// import AanvraagVeiligheidsVerificatieIndienenResultaat from "pages/requests/stepsIndividual/standalone-step5/step5";
import Error from "pages/home/error";
import { PrivateRoute } from "components/RouteLayouts/PrivateRoute";
import ListRequests from "pages/employee/requests/listRequests/listRequests";
import RequestDetails from "pages/employee/requests/requestDetails/requestDetails";
import Beheer from "pages/employee/beheer/beheer";
import FinalizeApplication from "pages/requests/stepsIndividual/standalone-step5/step5";
import ListCertificates from "pages/employee/certificates/listCertificates/listCertificates";
import Profile from "pages/external/profile/profile";

//? STYLES //
import * as Footer from "styles/components/footer.styles";

import * as Global from "styles/global/global.styles";
import CertificateDetails from "pages/employee/certificates/certificateDetails/certificateDetails";
import ListExports from "pages/employee/export/listExports/listExports";
import ListLocations from "pages/development/settings/tabs/locations/listLocations";
import Submitted from "pages/requests/stepsIndividual/submitted/submitted";
import DataCompletion from "pages/requests/dataCompletion";
import { useTranslation } from "react-i18next";
import ListRenews from "pages/employee/certificates/listRenews/listRenews";
import ListCompanies from "pages/development/settings/tabs/companies/listCompanies";
import ListResponsible from "pages/development/settings/tabs/companies/listResponsible";
import CompleteLogin from "pages/external/completeLogin/completeLogin";
import { AuthProvider } from "contexts/authContext";
import { SettingsProvider } from "contexts/settingsContext";
import { TenantProvider } from "contexts/tenantContext";
import Settings from "pages/development/settings/Settings";
import ListUsers from "pages/employee/users/listUsers/listUsers";
import UserDetails from "pages/employee/users/userDetails/userDetails";

declare module "@mui/material/styles" {
    interface Palette {
        delete: Palette["primary"];
        cancel: Palette["primary"];
    }

    interface PaletteOptions {
        delete?: PaletteOptions["primary"];
        cancel?: PaletteOptions["primary"];
    }
}

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        delete: true;
        cancel: true;
    }
}

const theme = createTheme({
    palette: {
        cancel: {
            light: "gray",
            main: "gray",
            dark: "#666666",
            contrastText: "#ffffff",
        },
        delete: {
            light: "#000000",
            main: "#000000",
            dark: "#212020",
            contrastText: "#ffffff",
        },
        primary: {
            light: "#00C6B6",
            main: "#0064B4",
            dark: "#00467d",
            contrastText: "#ffffff",
        },
    },
});

document.body.style.backgroundColor =
    Global.colors().__content.contentBackground;

function App() {
    const { t } = useTranslation();
    const location = useLocation();
    const path = location.pathname;
    const display = () => {
        return path.includes("/beheer") ? false : true;
    };

    const navigate = useNavigate();
    const { i18n } = useTranslation();

    useEffect(() => {}, [location, path]);

    useEffect(() => {
        // Will only run once when the component is mounted
        checkForDefaultLanguage();
    }, []);

    function checkForDefaultLanguage() {
        const selectedLanguage = localStorage.getItem("language");
        if (selectedLanguage === "" || selectedLanguage === null) {
            var languageUser = navigator.language;
            var selectedLanuage = "nl";
            if (languageUser.startsWith("fr-") || languageUser === "fr") {
                selectedLanuage = "fr";
            } else if (
                languageUser.startsWith("en-") ||
                languageUser === "en"
            ) {
                selectedLanuage = "en";
            }
            i18n.changeLanguage(selectedLanuage);
            localStorage.setItem("language", selectedLanuage);
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <AuthProvider>
                <TenantProvider>
                    <SettingsProvider>
                        <title>{t("home.heading")}</title>
                        <Box
                            sx={{
                                backgroundColor:
                                    Global.colors().__content.contentBackground,
                            }}
                        >
                            {/* //? Header */}
                            <Header />

                            {/* //? Content */}
                            <Routes>
                                <Route
                                    path="*"
                                    element={<Content page={Error} />}
                                />
                                <Route
                                    path="/"
                                    element={
                                        <Navigate
                                            to="/aanvraag_veiligheidsverificatie_indienen"
                                            replace
                                        />
                                    }
                                />
                                <Route
                                    path="/aanvraag_veiligheidsverificatie_indienen"
                                    element={<Content page={ZeroStep} />}
                                />

                                <Route
                                    path="/login"
                                    element={<Content page={Login} />}
                                />
                                <Route
                                    path="/unauthorized"
                                    element={<Content page={Unauthorized} />}
                                />
                                <Route
                                    path="/aanvraag_veiligheidsverificatie_indienen/:applicationId"
                                    element={
                                        <Content
                                            page={
                                                AanvraagVeiligheidsVerificatieIndienen
                                            }
                                        />
                                    }
                                />
                                <Route
                                    path="/aanvraag_veiligheidsverificatie_ingediend"
                                    element={<Content page={Submitted} />}
                                />
                                <Route
                                    path="/onvolledig/:applicationId/:step/:completionHash"
                                    element={
                                        <Content
                                            page={
                                                AanvraagVeiligheidsVerificatieIndienen
                                            }
                                            props={{
                                                titlePath: "requestAll.title",
                                            }}
                                        />
                                    }
                                />
                                <Route
                                    path="/complete/:applicationId/:completionHash"
                                    element={
                                        <Content
                                            page={
                                                AanvraagVeiligheidsVerificatieIndienen
                                            }
                                            props={{
                                                titlePath:
                                                    "requestAll.completeTitle",
                                            }}
                                        />
                                    }
                                />

                                {/* Pages for external users only */}
                                <Route
                                    path="/complete-login"
                                    element={<Content page={CompleteLogin} />}
                                />
                                <Route
                                    path="/profile"
                                    element={
                                        <PrivateRoute roles={["External"]}>
                                            <Content page={Profile} />
                                        </PrivateRoute>
                                    }
                                />

                                <Route
                                    path="/aanvraag_veiligheidsverificatie_indienen_andere/:applicationId"
                                    element={
                                        <PrivateRoute
                                            roles={[
                                                "OpstartenAanvraag",
                                                "Employee",
                                                "External",
                                            ]}
                                            inAllRoles={false}
                                        >
                                            <Content
                                                page={DataCompletion}
                                                props={{
                                                    titlePath:
                                                        "requestAll.startTitle",
                                                }}
                                            />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer"
                                    element={
                                        <PrivateRoute
                                            roles={["Employee", "External"]}
                                        >
                                            <Content page={Beheer} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/aanvragen"
                                    element={
                                        <PrivateRoute
                                            roles={[
                                                "BeheerAanvraag",
                                                "WeigerenAanvraag",
                                                "OpvolgenAanvragen",
                                                "Employee",
                                                "External",
                                            ]}
                                            inAllRoles={false}
                                        >
                                            <Content page={ListRequests} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/export"
                                    element={
                                        <PrivateRoute
                                            roles={["Exporteren"]}
                                            inAllRoles={true}
                                        >
                                            <Content page={ListExports} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/aanvragen/:applicationId"
                                    element={
                                        <PrivateRoute
                                            roles={[
                                                "BeheerAanvraag",
                                                "WeigerenAanvraag",
                                                "OpvolgenAanvragen",
                                                "Employee",
                                                "External",
                                            ]}
                                            inAllRoles={false}
                                        >
                                            <Content page={RequestDetails} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/attesten"
                                    element={
                                        <PrivateRoute
                                            roles={[
                                                "BeheerAttesten",
                                                "ControleAttesten",
                                                "IntrekkenAttesten",
                                                "OpvolgenAttesten",
                                                "Employee",
                                                "External",
                                            ]}
                                            inAllRoles={false}
                                        >
                                            <Content page={ListCertificates} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/vernieuwingen"
                                    element={
                                        <PrivateRoute
                                            roles={[
                                                "Administrator",
                                                "OpvolgenAttesten",
                                            ]}
                                            inAllRoles={false}
                                        >
                                            <Content page={ListRenews} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/vernieuwingen/:certificateId"
                                    element={
                                        <PrivateRoute
                                            roles={[
                                                "Administrator",
                                                "OpvolgenAttesten",
                                                "Extertnal",
                                            ]}
                                            inAllRoles={false}
                                        >
                                            <Content
                                                page={DataCompletion}
                                                props={{
                                                    titlePath:
                                                        "renew.completeTitle",
                                                }}
                                            />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/attesten/:certificateId"
                                    element={
                                        <PrivateRoute
                                            roles={[
                                                "BeheerAttesten",
                                                "ControleAttesten",
                                                "IntrekkenAttesten",
                                                "OpvolgenAttesten",
                                                "Employee",
                                                "External",
                                            ]}
                                            inAllRoles={false}
                                        >
                                            <Content
                                                page={CertificateDetails}
                                            />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/medewerkers"
                                    element={
                                        <PrivateRoute roles={["Administrator", "OpvolgenAttesten"]} inAllRoles={false}>
                                            <Content page={ListUsers} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/beheer/medewerkers/:userId"
                                    element={
                                        <PrivateRoute roles={["Administrator", "OpvolgenAttesten"]} inAllRoles={false}>
                                            <Content page={UserDetails} />
                                        </PrivateRoute>
                                    }
                                />
                                {/* <Route
                                    path='/beheer/companies'
                                    element={
                                        <PrivateRoute roles={["BeheerOrganisaties"]}>
                                            <Content page={ListCompanies} />
                                        </PrivateRoute>
                                    }
                                /> */}
                                <Route
                                    path="/beheer/settings"
                                    element={
                                        <PrivateRoute roles={["Administrator"]}>
                                            <Content page={Settings} />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/finalize/:applicationId"
                                    element={
                                        <Content page={FinalizeApplication} />
                                    }
                                />
                                <Route
                                    path="/finalize/:applicationId/:completionHash"
                                    element={
                                        <Content page={FinalizeApplication} />
                                    }
                                />
                            </Routes>

                            {/* //? Footer */}
                            {display() && (
                                <Box
                                    component="footer"
                                    sx={Footer.Container}
                                ></Box>
                            )}
                        </Box>
                    </SettingsProvider>
                </TenantProvider>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
